import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import DateQuickPresets from '../../components/DateQuickPresets';

const FluxoCaixaProjetado = () => {
  const hojeIso = format(new Date(), 'yyyy-MM-dd');
  const [carregando, setCarregando] = useState(false);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [dados, setDados] = useState({
    saldo_inicial: 0,
    saldo_final: 0,
    total_receitas: 0,
    total_despesas: 0,
    serie: [],
  });
  const [filtros, setFiltros] = useState({
    data_inicio: hojeIso,
    dias: 90,
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas',
    incluir_pendentes: true,
  });

  const formatarValor = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const carregarFiltros = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias'),
      ]);
      setCentrosCustos(Array.isArray(centrosResponse.data) ? centrosResponse.data : []);
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      console.error('Erro ao carregar filtros de fluxo projetado:', error);
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/fluxo-caixa-projetado', { params: filtros });
      const payload = response.data || {};
      setDados({
        saldo_inicial: Number(payload.saldo_inicial || 0),
        saldo_final: Number(payload.saldo_final || 0),
        total_receitas: Number(payload.total_receitas || 0),
        total_despesas: Number(payload.total_despesas || 0),
        serie: Array.isArray(payload.serie) ? payload.serie : [],
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar fluxo de caixa projetado');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarFiltros();
  }, []);

  useEffect(() => {
    carregarDados();
  }, [filtros]);

  const chartData = useMemo(
    () =>
      (dados.serie || []).map((item) => ({
        ...item,
        label: format(parseISO(item.dia), 'dd/MM'),
      })),
    [dados.serie]
  );

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Fluxo de Caixa Projetado</h1>
          <p className="text-gray-600">Projecao diaria com base no saldo atual e lancamentos futuros</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <DateQuickPresets
            mode="single"
            date={filtros.data_inicio}
            onSelect={({ date }) => setFiltros((old) => ({ ...old, data_inicio: date }))}
            className="mb-4"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="form-label">Data Inicial</label>
              <input
                type="date"
                className="form-input"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros((old) => ({ ...old, data_inicio: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Dias</label>
              <input
                type="number"
                min="1"
                max="365"
                className="form-input"
                value={filtros.dias}
                onChange={(e) => setFiltros((old) => ({ ...old, dias: Number(e.target.value || 90) }))}
              />
            </div>
            <div>
              <label className="form-label">Centro de Custo</label>
              <select
                className="form-select"
                value={filtros.centro_custo_id}
                onChange={(e) => setFiltros((old) => ({ ...old, centro_custo_id: e.target.value }))}
              >
                <option value="todos">Todos</option>
                {centrosCustos.map((centro) => (
                  <option key={centro.id} value={centro.id}>
                    {centro.codigo} - {centro.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Conta Bancaria</label>
              <select
                className="form-select"
                value={filtros.conta_bancaria_id}
                onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))}
              >
                <option value="todas">Todas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.banco} - {conta.conta}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={filtros.incluir_pendentes}
                  onChange={(e) => setFiltros((old) => ({ ...old, incluir_pendentes: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">Incluir pendentes</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Saldo Inicial</p>
            <p className="text-xl font-bold text-gray-900">R$ {formatarValor(dados.saldo_inicial)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Entradas Projetadas</p>
            <p className="text-xl font-bold text-success-700">R$ {formatarValor(dados.total_receitas)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Saidas Projetadas</p>
            <p className="text-xl font-bold text-danger-700">R$ {formatarValor(dados.total_despesas)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Saldo Final Projetado</p>
            <p className={`text-xl font-bold ${dados.saldo_final >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              R$ {formatarValor(dados.saldo_final)}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Saldo Projetado (diario)</h3>
          </div>
          <div className="card-body">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `R$ ${(Number(v) / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [`R$ ${formatarValor(v)}`, 'Saldo']} />
                  <Legend />
                  <Line type="monotone" dataKey="saldo_projetado" name="Saldo Projetado" stroke="#2563eb" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Entradas vs Saidas (diario)</h3>
          </div>
          <div className="card-body">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `R$ ${(Number(v) / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [`R$ ${formatarValor(v)}`, 'Valor']} />
                  <Legend />
                  <Bar dataKey="receitas" name="Entradas" fill="#16a34a" />
                  <Bar dataKey="despesas" name="Saidas" fill="#dc2626" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">Detalhamento Diario</h3>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Entradas</th>
                  <th>Saidas</th>
                  <th>Saldo do Dia</th>
                  <th>Saldo Projetado</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item) => (
                  <tr key={item.dia}>
                    <td>{format(parseISO(item.dia), 'dd/MM/yyyy')}</td>
                    <td className="text-success-700">R$ {formatarValor(item.receitas)}</td>
                    <td className="text-danger-700">R$ {formatarValor(item.despesas)}</td>
                    <td className={Number(item.saldo_dia) >= 0 ? 'text-success-700' : 'text-danger-700'}>R$ {formatarValor(item.saldo_dia)}</td>
                    <td className={Number(item.saldo_projetado) >= 0 ? 'text-success-700 font-semibold' : 'text-danger-700 font-semibold'}>
                      R$ {formatarValor(item.saldo_projetado)}
                    </td>
                  </tr>
                ))}
                {chartData.length === 0 && !carregando && (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-500 py-8">
                      Nenhum dado encontrado para os filtros selecionados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FluxoCaixaProjetado;
